import * as React from 'react';
import { Link } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Btn, EmptyState, Section, Kpi, Pill, Table, Th, Td, fmt } from '../components/ui-kit';

interface Contract { id: string; title: string; status: string; updated_at: string; }

export default function ContractsList() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Contract[] | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [q, setQ] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<{ contracts: Contract[] }>('/api/v1/exec/contracts', tok);
        setItems(j.contracts ?? []);
      } catch (e: any) {
        setErr(e.message);
        setItems([]);
      }
    })();
  }, [getToken]);

  const filtered = (items ?? []).filter((contract) => !q || [contract.title, contract.id, contract.status].some((value) => (value ?? '').toLowerCase().includes(q.toLowerCase())));
  const signedCount = filtered.filter((contract) => (contract.status ?? '').toLowerCase() === 'signed').length;
  const pendingCount = filtered.filter((contract) => (contract.status ?? '').toLowerCase() !== 'signed').length;

  return (
    <div>
      <PageTitle kicker="Executive Room" title="Contracts" />

      <Section
        title="Contract control"
        right={(<Link to="/contracts/draft"><Btn primary>+ New draft</Btn></Link>)}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Visible contracts" value={fmt(filtered.length)} hint={q ? 'filtered view' : 'full list'} />
          <Kpi accent="green" label="Signed" value={fmt(signedCount)} />
          <Kpi accent="amber" label="Pending" value={fmt(pendingCount)} />
          <Kpi label="Draft route" value="ready" hint="/contracts/draft" />
        </div>
      </Section>

      <Section title="Workflow" hint="Review each record before triggering signature or finance handoff.">
        <div className="grid gap-4 lg:grid-cols-[1.35fr,0.65fr]">
          <Card>
            <p className="text-sm text-white">Executive Room tracks the contract lifecycle from draft to signed record.</p>
            <p className="text-xs text-white/60 mt-2 leading-relaxed">
              Open the review page to inspect payload, signature readiness, and status transitions. Signed contracts remain listed here as the legal audit trail.
            </p>
          </Card>
          <Card>
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search title, id, status…"
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
              aria-label="Search contracts"
            />
          </Card>
        </div>
      </Section>

      <Section title="Contract ledger">
        {items === null && <Card>Loading…</Card>}
        {err && <Card className="border-red-500/30 text-red-300 text-sm">{err}</Card>}
        {items && items.length === 0 && (
          <EmptyState title="No contracts yet" hint="Start with a new draft." />
        )}
        {items && items.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Contract</Th><Th>Status</Th><Th>Updated</Th><Th className="text-right">Action</Th></tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td>
                    <p className="text-sm font-bold text-white">{c.title}</p>
                    <p className="text-xs text-white/40 mt-1 font-mono">{c.id}</p>
                  </Td>
                  <Td><Pill tone={(c.status ?? '').toLowerCase() === 'signed' ? 'live' : 'warn'}>{c.status}</Pill></Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{c.updated_at?.slice(0, 10) ?? '—'}</span></Td>
                  <Td className="text-right">
                    <Link to={`/contracts/${c.id}/review`}>
                      <Btn>Review</Btn>
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
